import { useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  createEmptyNav,
  createFirstAvailableLayerNav,
  createScopedNav,
} from "../../common/slices/specsEditor/helpers";
import { specsEditorActions } from "../../common/slices/specsEditor/slice";
import type { SpecsModel } from "./specsModel/SpecsModel";
import type {
  SpecsEditorNav,
  SpecsEditorSession,
  SpecsLayer,
  SpecsLayerModelPath,
} from "./types";
import { useSpecsEditorLayerModelPaths } from "./useSpecsEditorLayerModelPaths";

type ElementNav = Extract<
  SpecsEditorNav,
  { kind: "element" } | { kind: "layer" }
>;

type UseSpecsEditorElementsArgs = {
  currentVersion: ReturnType<SpecsModel["getVersion"]> | null;
  layers: SpecsLayer[];
  selectedNav: SpecsEditorSession["nav"];
  session: SpecsEditorSession;
};

function getNavForElement(
  nav: SpecsEditorNav,
  elementName: string,
  partName?: string
): ElementNav | null {
  switch (nav.kind) {
    case "element":
    case "layer":
      return nav.element === elementName && (!partName || nav.part === partName)
        ? nav
        : null;
    default:
      return null;
  }
}

function layerPathMatchesElement(
  path: SpecsLayerModelPath | null,
  elementName: string,
  partName?: string
) {
  if (!path || path.elementName !== elementName) {
    return false;
  }

  return !partName || path.partName === partName;
}

function clearLayerElement(
  path: SpecsLayerModelPath | null,
  elementName: string,
  partName?: string
) {
  if (!path || !layerPathMatchesElement(path, elementName, partName)) {
    return path;
  }

  return { ...path, elementName: "", styleName: "" };
}

function renameLayerElement(args: {
  path: SpecsLayerModelPath | null;
  previousName: string;
  nextName: string;
  partName?: string;
}) {
  const { path, previousName, nextName, partName } = args;

  if (!path || !layerPathMatchesElement(path, previousName, partName)) {
    return path;
  }

  return { ...path, elementName: nextName };
}

export function useSpecsEditorElements({
  currentVersion,
  layers,
  selectedNav,
  session,
}: UseSpecsEditorElementsArgs) {
  const dispatch = useDispatch();
  const updateLayerModelPaths = useSpecsEditorLayerModelPaths(layers);

  const addElement = useCallback(
    (name: string, partName?: string) => {
      currentVersion?.addElement(name, partName || "body");
    },
    [currentVersion]
  );

  const removeElement = useCallback(
    (name: string, partName?: string) => {
      currentVersion?.removeElement(name, partName);
      updateLayerModelPaths((path) => clearLayerElement(path, name, partName));

      if (!getNavForElement(selectedNav, name, partName)) {
        return;
      }

      dispatch(
        specsEditorActions.setNav(
          createFirstAvailableLayerNav(session) ?? createEmptyNav(session)
        )
      );
    },
    [currentVersion, dispatch, selectedNav, session, updateLayerModelPaths]
  );

  const updateElement = useCallback(
    (
      name: string,
      patch: Partial<{
        name: string;
        inherit: boolean;
        inheritFromGroup: string;
        displayable: boolean;
        menuIconUrl: string;
      }>,
      partName?: string
    ) => {
      currentVersion?.updateElement(name, patch, partName);
      if (patch.name === undefined) {
        return;
      }
      const nextName = patch.name;

      updateLayerModelPaths((path) =>
        renameLayerElement({
          path,
          previousName: name,
          nextName,
          partName,
        })
      );

      const scopedNav = getNavForElement(selectedNav, name, partName);
      if (!scopedNav) {
        return;
      }

      dispatch(
        specsEditorActions.setNav(
          createScopedNav(session, scopedNav.part, nextName, scopedNav.style)
        )
      );
    },
    [currentVersion, dispatch, selectedNav, session, updateLayerModelPaths]
  );

  return {
    addElement,
    removeElement,
    updateElement,
  };
}
